import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { BRAND, SPACES, FAQS } from "@/lib/site";
import SEO, { breadcrumbsLd, faqLd } from "@/components/site/SEO";
import InquiryForm from "@/components/site/InquiryForm";
import ContactPanel from "@/components/site/ContactPanel";
import FAQSection from "@/components/site/FAQSection";
import FinalCTA from "@/components/site/FinalCTA";

/**
 * Generic, data-driven landing page used for long-tail SEO queries.
 * Pass a `preset` describing the niche; the page composes a focused, on-keyword
 * landing experience that funnels into the standard lead form.
 */
export const NicheLandingPage = ({
  slug,
  h1,
  metaTitle,
  metaDescription,
  intro,
  audience,
  benefits,
  recommendedSlugs = ["private-offices", "executive-suites", "office-suites"],
  heroImage,
  defaultOfficeType,
}) => {
  const recommended = recommendedSlugs
    .map((s) => SPACES.find((sp) => sp.slug === s))
    .filter(Boolean);

  return (
    <>
      <SEO
        title={metaTitle}
        description={metaDescription}
        path={`/${slug}`}
        image={heroImage}
        jsonLd={[
          breadcrumbsLd([
            { name: "Home", path: "/" },
            { name: h1, path: `/${slug}` },
          ]),
          faqLd(FAQS),
        ]}
      />

      {/* HERO */}
      <section className="bg-[var(--bg)] border-b border-[var(--border)]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20 py-20 md:py-28">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-7">
              <div className="eyebrow">Weston, Florida</div>
              <h1 className="hero-display text-[44px] md:text-[80px] mt-5 leading-[1.0]">
                {h1}
              </h1>
              <p className="mt-7 max-w-xl text-[16px] leading-relaxed text-[var(--text-muted)]">
                {intro}
              </p>
              <div className="mt-9 flex flex-col sm:flex-row gap-3">
                <Link to={`/contact?type=${encodeURIComponent(defaultOfficeType || "Not sure yet — show me everything")}`} className="btn-primary justify-center">
                  Schedule a tour
                  <ArrowRight size={14} strokeWidth={2.2} />
                </Link>
                <a href={`tel:${BRAND.phoneRaw}`} className="btn-ghost justify-center">
                  Call {BRAND.phone}
                </a>
              </div>
            </div>
            <div className="md:col-span-5">
              <div className="hairline aspect-[4/3] overflow-hidden">
                <img src={heroImage} alt={h1} className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AUDIENCE + BENEFITS */}
      <section className="bg-white border-b border-[var(--border)]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20 py-20 md:py-28">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-5">
              <div className="eyebrow">Who this is for</div>
              <h2 className="section-title text-[34px] md:text-[46px] mt-4 leading-tight">
                Built for the way you run your practice.
              </h2>
              <ul className="mt-8 space-y-3">
                {audience.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-[15px]">
                    <span className="w-7 h-px bg-[var(--accent)] mt-3 shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="md:col-span-7">
              <div className="eyebrow">Why tenants like you choose us</div>
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
                {benefits.map((b) => (
                  <div key={b.title} className="flex items-start gap-3">
                    <Check size={16} strokeWidth={2.1} className="mt-1 text-[var(--primary)]" />
                    <div>
                      <div className="font-serif text-[18px] leading-tight">{b.title}</div>
                      <div className="text-[13.5px] text-[var(--text-muted)] mt-1 leading-relaxed">{b.body}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* RECOMMENDED SPACES */}
      <section className="bg-[var(--bg)] border-b border-[var(--border)]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20 py-20 md:py-28">
          <div className="eyebrow">Recommended spaces</div>
          <h2 className="section-title text-[34px] md:text-[52px] mt-4 leading-tight">
            Three configurations we&apos;d suggest first.
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {recommended.map((s) => (
              <Link
                key={s.slug}
                to={`/spaces/${s.slug}`}
                className="hairline bg-white overflow-hidden group"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={s.image} alt={s.name} className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.04]" loading="lazy" />
                </div>
                <div className="p-6">
                  <div className="text-[11px] tracking-[0.18em] uppercase text-[var(--text-muted)]">{s.eyebrow}</div>
                  <h3 className="font-serif text-[24px] mt-3 leading-tight">{s.name}</h3>
                  <p className="text-[13.5px] text-[var(--text-muted)] mt-2 leading-relaxed">{s.short}</p>
                  <div className="mt-5 flex items-center justify-between text-[12px]">
                    <span className="text-[var(--text-muted)] uppercase tracking-wider">From</span>
                    <span className="font-serif text-[22px] text-[var(--primary)]">{s.startingRate}<span className="text-[12px] text-[var(--text-muted)] font-normal"> {s.rateUnit}</span></span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* INQUIRY */}
      <section className="bg-[var(--bg)] border-b border-[var(--border)]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20 py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            <div className="lg:col-span-7">
              <InquiryForm
                defaultOfficeType={defaultOfficeType || ""}
                sourcePage={`landing:${slug}`}
                heading={`Inquire — ${h1.replace(/\.$/, "")}`}
                subheading="One form, one response. We'll suggest two or three suites that fit your practice and book a 30-minute walk-through."
              />
            </div>
            <div className="lg:col-span-5">
              <ContactPanel />
            </div>
          </div>
        </div>
      </section>

      <FAQSection />
      <FinalCTA />
    </>
  );
};

export default NicheLandingPage;
